import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { SharedModule } from './shared/shared.module';
import {
  CommonModule,
  LOCATION_INITIALIZED,
  registerLocaleData,
} from '@angular/common';
import { ChargerOnboarderModule } from './charger-onboarder/charger-onboarder.module';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import localeEnGb from '@angular/common/locales/en-GB';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import { take } from 'rxjs';
import { WithCredentialsInterceptor } from './core/with-credentials.interceptor';
import {
  isLanguageCodeSupported,
  supportedLanguageCodes,
} from './shared/data/languages.data';
import { CustomTranslateLoader } from './core/custom-translation-loader';

registerLocaleData(localeEnGb, 'en-GB');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeFr, 'fr');

export function createTranslateLoader(http: HttpClient) {
  return new CustomTranslateLoader(http);
}

export function appInitializerFactory(
  translate: TranslateService,
  injector: Injector
) {
  return () =>
    new Promise((resolve) => {
      const locationInitialized = injector.get(
        LOCATION_INITIALIZED,
        Promise.resolve(null)
      );

      locationInitialized.then(() => {
        translate.addLangs(supportedLanguageCodes);
        let languageCode: string = isLanguageCodeSupported(
          translate.getBrowserLang()
        )
          ? (translate.getBrowserLang() as string)
          : 'en-GB';

        translate.setDefaultLang(languageCode);

        if (window.localStorage.getItem('translationKeys') === 'true') {
          translate.setDefaultLang('translationKeys');
          languageCode = 'translationKeys';
        }

        translate
          .use(languageCode)
          .pipe(take(1))
          .subscribe({
            next: () => {
              console.info(
                `Successfully initialized '${languageCode}' language.'`
              );
            },
            error: (err) => {
              console.error(
                `Problem with '${languageCode}' language initialization.'`,
                err
              );
            },
            complete: () => {
              resolve(null);
            },
          });
      });
    });
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    ChargerOnboarderModule,
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    provideAnimationsAsync(),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WithCredentialsInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
